"use client";

import Image from "next/image";
import { useState } from "react";
import { useTranslations } from "next-intl";

import { Link } from "@/navigation";

const IMAGE_BASE = "/image/new-version/ValueAddedSolutions/industrial-automation/Capabilities";

const TABS = [
	{ id: "processes", labelKey: "manufacturingCapabilitiesProcessesTab" },
	{ id: "materials", labelKey: "manufacturingCapabilitiesMaterialsTab" },
	{ id: "finishes", labelKey: "manufacturingCapabilitiesFinishesTab" },
] as const;

const PROCESSES = [
	{
		keyPrefix: "manufacturingCapabilitiesProcess1",
		image: `${IMAGE_BASE}/Processes/cnc-machining.webp`,
		href: "/cnc-machining",
	},
	{
		keyPrefix: "manufacturingCapabilitiesProcess2",
		image: `${IMAGE_BASE}/Processes/injection-molding.webp`,
		href: "/injection-molding",
	},
	{
		keyPrefix: "manufacturingCapabilitiesProcess3",
		image: `${IMAGE_BASE}/Processes/sheet-metal-fabrication.webp`,
		href: "/sheet-metal-fabrication",
	},
	{
		keyPrefix: "manufacturingCapabilitiesProcess4",
		image: `${IMAGE_BASE}/Processes/3d-printing.webp`,
		href: "/3d-printing",
	},
	{
		keyPrefix: "manufacturingCapabilitiesProcess5",
		image: `${IMAGE_BASE}/Processes/extrusion.webp`,
		href: "/aluminum-extrusion",
	},
	{
		keyPrefix: "manufacturingCapabilitiesProcess6",
		image: `${IMAGE_BASE}/Processes/die-casting.webp`,
		href: "/die-casting",
	},
	{
		keyPrefix: "manufacturingCapabilitiesProcess7",
		image: `${IMAGE_BASE}/Processes/vacuum-casting.webp`,
		href: "/vacuum-casting",
	},
	{
		keyPrefix: "manufacturingCapabilitiesProcess8",
		image: `${IMAGE_BASE}/Processes/surface-finishing.webp`,
		href: "/surface-finishing",
	},
] as const;

const MATERIAL_GROUPS = [
	{
		titleKey: "manufacturingCapabilitiesMetalsTitle",
		items: [
			"manufacturingCapabilitiesMetal1",
			"manufacturingCapabilitiesMetal2",
			"manufacturingCapabilitiesMetal3",
			"manufacturingCapabilitiesMetal4",
			"manufacturingCapabilitiesMetal5",
			"manufacturingCapabilitiesMetal6",
			"manufacturingCapabilitiesMetal7",
			"manufacturingCapabilitiesMetal8",
			"manufacturingCapabilitiesMetal9",
			"manufacturingCapabilitiesMetal10",
		],
	},
	{
		titleKey: "manufacturingCapabilitiesPlasticsTitle",
		items: [
			"manufacturingCapabilitiesPlastic1",
			"manufacturingCapabilitiesPlastic2",
			"manufacturingCapabilitiesPlastic3",
			"manufacturingCapabilitiesPlastic4",
			"manufacturingCapabilitiesPlastic5",
			"manufacturingCapabilitiesPlastic6",
			"manufacturingCapabilitiesPlastic7",
			"manufacturingCapabilitiesPlastic8",
			"manufacturingCapabilitiesPlastic9",
			"manufacturingCapabilitiesPlastic10",
			"manufacturingCapabilitiesPlastic11",
			"manufacturingCapabilitiesPlastic12",
			"manufacturingCapabilitiesPlastic13",
			"manufacturingCapabilitiesPlastic14",
			"manufacturingCapabilitiesPlastic15",
			"manufacturingCapabilitiesPlastic16",
			"manufacturingCapabilitiesPlastic17",
			"manufacturingCapabilitiesPlastic18",
			"manufacturingCapabilitiesPlastic19",
			"manufacturingCapabilitiesPlastic20",
			"manufacturingCapabilitiesPlastic21",
			"manufacturingCapabilitiesPlastic22",
			"manufacturingCapabilitiesPlastic23",
			"manufacturingCapabilitiesPlastic24",
		],
	},
] as const;

const FINISHES = [
	{ keyPrefix: "manufacturingCapabilitiesFinish1", image: `${IMAGE_BASE}/finishes/as-machined.webp` },
	{ keyPrefix: "manufacturingCapabilitiesFinish2", image: `${IMAGE_BASE}/finishes/anodizing.webp` },
	{ keyPrefix: "manufacturingCapabilitiesFinish3", image: `${IMAGE_BASE}/finishes/brushed-finish.webp` },
	{ keyPrefix: "manufacturingCapabilitiesFinish4", image: `${IMAGE_BASE}/finishes/sand-blasting.webp` },
	{ keyPrefix: "manufacturingCapabilitiesFinish5", image: `${IMAGE_BASE}/finishes/heat-treatment.webp` },
	{ keyPrefix: "manufacturingCapabilitiesFinish6", image: `${IMAGE_BASE}/finishes/teflon-coating.webp` },
	{ keyPrefix: "manufacturingCapabilitiesFinish7", image: `${IMAGE_BASE}/finishes/black-oxidize.webp` },
	{ keyPrefix: "manufacturingCapabilitiesFinish8", image: `${IMAGE_BASE}/finishes/electroless-plating.webp` },
	{ keyPrefix: "manufacturingCapabilitiesFinish9", image: `${IMAGE_BASE}/finishes/electroplating.webp` },
	{ keyPrefix: "manufacturingCapabilitiesFinish10", image: `${IMAGE_BASE}/finishes/painting.webp` },
	{ keyPrefix: "manufacturingCapabilitiesFinish11", image: `${IMAGE_BASE}/finishes/powder-coating.webp` },
	{ keyPrefix: "manufacturingCapabilitiesFinish12", image: `${IMAGE_BASE}/finishes/electrophoresis.webp` },
	{ keyPrefix: "manufacturingCapabilitiesFinish13", image: `${IMAGE_BASE}/finishes/tumbling.webp` },
	{ keyPrefix: "manufacturingCapabilitiesFinish14", image: `${IMAGE_BASE}/finishes/alodine.webp` },
	{ keyPrefix: "manufacturingCapabilitiesFinish15", image: `${IMAGE_BASE}/finishes/electropolish.webp` },
	{ keyPrefix: "manufacturingCapabilitiesFinish16", image: `${IMAGE_BASE}/finishes/passivation.webp` },
	{ keyPrefix: "manufacturingCapabilitiesFinish17", image: `${IMAGE_BASE}/finishes/spi-finish.webp` },
] as const;

type ActiveTab = (typeof TABS)[number]["id"];

const ArrowIcon = ({ className = "h-5 w-5" }: { className?: string }) => (
	<svg className={className} viewBox="0 0 20 20" fill="none" aria-hidden="true">
		<path d="M4 10h11M11 6l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

export default function IndustrialAutomationManufacturingCapabilities() {
	const t = useTranslations("new-industrial-automation");
	const [activeTab, setActiveTab] = useState<ActiveTab>("processes");

	return (
		<section className="bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<header className="mb-6 text-center sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
						{t("manufacturingCapabilitiesTitle")}
					</h2>
					<div
						className="mt-5 flex flex-wrap justify-center gap-3 sm:mt-6 sm:gap-4"
						role="tablist"
						aria-label={t("manufacturingCapabilitiesTabsAriaLabel")}
					>
						{TABS.map((tab) => {
							const isActive = activeTab === tab.id;

							return (
								<button
									key={tab.id}
									type="button"
									role="tab"
									aria-selected={isActive}
									onClick={() => setActiveTab(tab.id)}
									className={`rounded border px-5 py-2 text-sm transition-colors sm:px-6 sm:text-base ${
										isActive
											? "border-themePrimary400 bg-themePrimary400 text-white"
											: "border-themePrimary400/40 bg-white text-themePrimary400 hover:border-themePrimary400 hover:bg-[#f5ffd4]"
									}`}
								>
									{t(tab.labelKey)}
								</button>
							);
						})}
					</div>
				</header>

				{activeTab === "processes" && <ProcessesPanel t={t} />}
				{activeTab === "materials" && <MaterialsPanel t={t} />}
				{activeTab === "finishes" && <FinishesPanel t={t} />}
			</div>
		</section>
	);
}

function ProcessesPanel({ t }: { t: ReturnType<typeof useTranslations<"new-industrial-automation">> }) {
	return (
		<div>
			<p className="mb-6 text-center text-sm leading-relaxed text-black md:mb-8 md:text-base">
				{t("manufacturingCapabilitiesProcessesDesc")}
			</p>
			<div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
				{PROCESSES.map((process) => (
					<Link
						key={process.keyPrefix}
						href={process.href}
						className="group relative overflow-hidden bg-[#d9d9d9] text-black transition-transform duration-300 hover:-translate-y-1 hover:text-black"
					>
						<div className="relative aspect-[4/3]">
							<Image
								src={process.image}
								alt={t(`${process.keyPrefix}ImageAlt`)}
								fill
								className="object-cover transition-transform duration-300 group-hover:scale-105"
								sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
							/>
						</div>
						<div className="bg-[#1f1f1f] p-4 text-white">
							<div className="flex items-center justify-between gap-3">
								<h3 className="text-base font-semibold sm:text-lg">{t(`${process.keyPrefix}Title`)}</h3>
								<ArrowIcon className="h-5 w-5 shrink-0 transition-transform duration-300 group-hover:translate-x-1" />
							</div>
						</div>
						<div className="pointer-events-none absolute inset-x-0 bottom-0 translate-y-3 bg-[#1f1f1f] p-4 text-white opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100 group-focus:translate-y-0 group-focus:opacity-100">
							<div className="flex items-start justify-between gap-3">
								<h3 className="text-base font-semibold sm:text-lg">{t(`${process.keyPrefix}Title`)}</h3>
								<ArrowIcon className="h-5 w-5 shrink-0 transition-transform duration-300 group-hover:translate-x-1" />
							</div>
							<p className="mt-2 text-xs leading-relaxed text-white/80">
								{t(`${process.keyPrefix}Desc`)}
							</p>
						</div>
					</Link>
				))}
			</div>
		</div>
	);
}

function MaterialsPanel({ t }: { t: ReturnType<typeof useTranslations<"new-industrial-automation">> }) {
	return (
		<div>
			<p className="mb-8 text-center text-sm leading-relaxed text-black md:text-base">
				{t("manufacturingCapabilitiesMaterialsDesc")}
			</p>
			<div className="grid gap-10 lg:grid-cols-2 lg:gap-16">
				{MATERIAL_GROUPS.map((group) => (
					<div key={group.titleKey}>
						<h3 className="mb-5 text-lg font-bold text-black sm:text-xl">{t(group.titleKey)}</h3>
						<div className="flex flex-wrap gap-3 sm:gap-4">
							{group.items.map((itemKey) => (
								<span key={itemKey} className="rounded-full bg-[#f0f0f0] px-5 py-2 text-sm text-black shadow-sm md:text-base">
									{t(itemKey)}
								</span>
							))}
						</div>
					</div>
				))}
			</div>
			<p className="mt-8 text-sm leading-relaxed text-black md:mt-10 md:text-base">
				{t("manufacturingCapabilitiesMaterialsNote")}
			</p>
		</div>
	);
}

function FinishesPanel({ t }: { t: ReturnType<typeof useTranslations<"new-industrial-automation">> }) {
	return (
		<div>
			<p className="mb-6 text-center text-sm leading-relaxed text-black md:mb-8 md:text-base">
				{t("manufacturingCapabilitiesFinishesDesc")}
			</p>
			<div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
				{FINISHES.map((finish) => (
					<article key={finish.keyPrefix} className="group overflow-hidden rounded-xl bg-[#f0f0f0]">
						<div className="relative aspect-[6/5]">
							<Image
								src={finish.image}
								alt={t(`${finish.keyPrefix}ImageAlt`)}
								fill
								className="object-cover transition-transform duration-300 group-hover:scale-105"
								sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 20vw"
							/>
							<div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-2 bg-white/70 px-3 py-2 text-black backdrop-blur-[1px]">
								<h3 className="text-sm font-medium sm:text-base">{t(`${finish.keyPrefix}Title`)}</h3>
								<ArrowIcon className="h-5 w-5 shrink-0" />
							</div>
						</div>
					</article>
				))}
			</div>
			<p className="mt-8 text-sm leading-relaxed text-black md:mt-10 md:text-base">
				{t("manufacturingCapabilitiesFinishesNote")}
			</p>
		</div>
	);
}
